import React, { useState, useEffect, useMemo } from 'react';
import { Hash, Copy, Download, Shuffle, Eye, Settings, List, MoreHorizontal, AlignLeft } from 'lucide-react';

interface NumberingStyle {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface NumberingOptions {
  startNumber: number;
  increment: number;
  padWithZeros: boolean;
  minDigits: number;
  prefix: string;
  suffix: string;
  separator: string;
  alignment: 'left' | 'right' | 'center';
  includeEmptyLines: boolean;
  skipEmptyLines: boolean;
  restartOnEmptyLine: boolean;
  customFormat: string;
  addSpacing: boolean;
  spacingCount: number;
  useCustomSeparator: boolean;
  removeExistingNumbers: boolean;
  highlightNumbers: boolean;
  addBrackets: boolean;
  bracketStyle: '()' | '[]' | '{}' | '<>';
  reverseNumbering: boolean;
  groupEvery: number;
  groupSeparator: string;
  onlyNumberNonEmpty: boolean;
}

interface NumberingResult {
  originalText: string;
  numberedText: string;
  statistics: NumberingStats;
}

interface NumberingStats {
  totalLines: number;
  numberedLines: number;
  emptyLines: number;
  longestLine: string;
  shortestLine: string;
  averageLineLength: number;
  maxLineNumber: number;
  totalCharacters: number;
  addedCharacters: number;
  characterIncrease: number;
}

const LineNumberer: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('decimal');
  const [outputText, setOutputText] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showStats, setShowStats] = useState(false);

  const [options, setOptions] = useState<NumberingOptions>({
    startNumber: 1,
    increment: 1,
    padWithZeros: false,
    minDigits: 1,
    prefix: '',
    suffix: '',
    separator: '. ',
    alignment: 'left',
    includeEmptyLines: true,
    skipEmptyLines: false,
    restartOnEmptyLine: false,
    customFormat: '{number}{separator}{line}',
    addSpacing: false,
    spacingCount: 4,
    useCustomSeparator: false,
    removeExistingNumbers: false,
    highlightNumbers: false,
    addBrackets: false,
    bracketStyle: '()',
    reverseNumbering: false,
    groupEvery: 0,
    groupSeparator: ',',
    onlyNumberNonEmpty: false,
  });

  const numberingStyles: NumberingStyle[] = [
    {
      id: 'decimal',
      name: 'Decimal (1, 2, 3)',
      description: 'Standard decimal numbering',
      icon: <Hash className="w-4 h-4" />,
      color: 'blue'
    },
    {
      id: 'roman',
      name: 'Roman (I, II, III)',
      description: 'Roman numeral numbering',
      icon: <List className="w-4 h-4" />,
      color: 'green'
    },
    {
      id: 'alpha',
      name: 'Alphabetic (a, b, c)',
      description: 'Lowercase alphabetic numbering',
      icon: <AlignLeft className="w-4 h-4" />,
      color: 'purple'
    },
    {
      id: 'alphaUpper',
      name: 'Uppercase (A, B, C)',
      description: 'Uppercase alphabetic numbering',
      icon: <AlignLeft className="w-4 h-4" />,
      color: 'red'
    },
    {
      id: 'binary',
      name: 'Binary (1, 10, 11)',
      description: 'Binary numbering system',
      icon: <Hash className="w-4 h-4" />,
      color: 'yellow'
    },
    {
      id: 'hexadecimal',
      name: 'Hexadecimal (1, 2, 3, A)',
      description: 'Hexadecimal numbering',
      icon: <Hash className="w-4 h-4" />,
      color: 'indigo'
    },
    {
      id: 'octal',
      name: 'Octal (1, 2, 3, 4)',
      description: 'Octal numbering system',
      icon: <Hash className="w-4 h-4" />,
      color: 'pink'
    },
    {
      id: 'bullets',
      name: 'Bullet Points (•)',
      description: 'Bullet point markers',
      icon: <MoreHorizontal className="w-4 h-4" />,
      color: 'orange'
    },
    {
      id: 'dashes',
      name: 'Dashes (-)',
      description: 'Dash markers for each line',
      icon: <MoreHorizontal className="w-4 h-4" />,
      color: 'teal'
    },
    {
      id: 'asterisks',
      name: 'Asterisks (*)',
      description: 'Asterisk markers',
      icon: <MoreHorizontal className="w-4 h-4" />,
      color: 'cyan'
    },
    {
      id: 'arrows',
      name: 'Arrows (→)',
      description: 'Arrow markers for each line',
      icon: <MoreHorizontal className="w-4 h-4" />,
      color: 'emerald'
    },
    {
      id: 'custom',
      name: 'Custom Format',
      description: 'User-defined numbering format',
      icon: <Settings className="w-4 h-4" />,
      color: 'violet'
    }
  ];

  const processNumbering = (text: string, style: string, opts: NumberingOptions): NumberingResult => {
    if (!text) {
      return {
        originalText: text,
        numberedText: text,
        statistics: createEmptyStats()
      };
    }

    let lines = text.split('\n');
    
    // Remove existing numbers if requested
    if (opts.removeExistingNumbers) {
      lines = lines.map(line => line.replace(/^\s*\d+[\.\)\]\}]?\s*/, ''));
    }

    const originalLength = text.length;
    let numberedLines: string[] = [];
    let currentNumber = opts.startNumber;

    lines.forEach((line, index) => {
      const isEmptyLine = line.trim() === '';
      
      // Determine if this line should be numbered
      let shouldNumber = true;
      if (opts.skipEmptyLines && isEmptyLine) {
        shouldNumber = false;
      }
      if (opts.onlyNumberNonEmpty && isEmptyLine) {
        shouldNumber = false;
      }

      if (shouldNumber) {
        const numberStr = generateNumber(currentNumber, style, opts);
        const formattedLine = formatNumberedLine(numberStr, line, opts);
        numberedLines.push(formattedLine);
        
        // Increment number
        currentNumber += opts.increment;
        
        // Restart numbering on empty line if requested
        if (opts.restartOnEmptyLine && isEmptyLine) {
          currentNumber = opts.startNumber;
        }
      } else {
        // Include line without numbering
        if (opts.includeEmptyLines || !isEmptyLine) {
          numberedLines.push(line);
        }
      }
    });

    // Apply reverse numbering if requested
    if (opts.reverseNumbering) {
      numberedLines = applyReverseNumbering(lines, style, opts);
    }

    const numberedText = numberedLines.join('\n');
    const statistics = calculateNumberingStats(text, numberedText, lines, numberedLines, currentNumber - opts.increment);

    return {
      originalText: text,
      numberedText,
      statistics
    };
  };

  const generateNumber = (num: number, style: string, opts: NumberingOptions): string => {
    let numberStr = '';

    switch (style) {
      case 'decimal':
        numberStr = num.toString();
        break;
      case 'roman':
        numberStr = toRoman(num);
        break;
      case 'alpha':
        numberStr = toAlpha(num, false);
        break;
      case 'alphaUpper':
        numberStr = toAlpha(num, true);
        break;
      case 'binary':
        numberStr = num.toString(2);
        break;
      case 'hexadecimal':
        numberStr = num.toString(16).toUpperCase();
        break;
      case 'octal':
        numberStr = num.toString(8);
        break;
      case 'bullets':
        numberStr = '•';
        break;
      case 'dashes':
        numberStr = '-';
        break;
      case 'asterisks':
        numberStr = '*';
        break;
      case 'arrows':
        numberStr = '→';
        break;
      case 'custom':
        numberStr = num.toString();
        break;
      default:
        numberStr = num.toString();
    }

    // Apply zero padding for numeric styles
    if (['decimal', 'binary', 'hexadecimal', 'octal'].includes(style) && opts.padWithZeros) {
      numberStr = numberStr.padStart(opts.minDigits, '0');
    }

    // Add grouping for large numbers
    if (opts.groupEvery > 0 && ['decimal'].includes(style) && numberStr.length > opts.groupEvery) {
      numberStr = addGroupingSeparator(numberStr, opts.groupEvery, opts.groupSeparator);
    }

    return numberStr;
  };

  const formatNumberedLine = (numberStr: string, line: string, opts: NumberingOptions): string => {
    let formatted = numberStr;

    // Add prefix and suffix
    formatted = opts.prefix + formatted + opts.suffix;

    // Add brackets
    if (opts.addBrackets) {
      const brackets = {
        '()': ['(', ')'],
        '[]': ['[', ']'],
        '{}': ['{', '}'],
        '<>': ['<', '>']
      };
      const [open, close] = brackets[opts.bracketStyle];
      formatted = open + formatted + close;
    }

    // Apply custom format if using custom style
    if (opts.customFormat && opts.customFormat.includes('{number}')) {
      return opts.customFormat
        .replace('{number}', formatted)
        .replace('{separator}', opts.separator)
        .replace('{line}', line);
    }

    // Add separator and line content
    let result = formatted + opts.separator + line;

    // Add spacing
    if (opts.addSpacing) {
      const spaces = ' '.repeat(opts.spacingCount);
      result = formatted + spaces + line;
    }

    return result;
  };

  const applyReverseNumbering = (lines: string[], style: string, opts: NumberingOptions): string[] => {
    const totalLines = lines.filter(line => !opts.onlyNumberNonEmpty || line.trim() !== '').length;
    let currentNumber = totalLines;
    
    return lines.map(line => {
      const isEmptyLine = line.trim() === '';
      
      if (opts.onlyNumberNonEmpty && isEmptyLine) {
        return line;
      }
      
      const numberStr = generateNumber(currentNumber, style, opts);
      const formatted = formatNumberedLine(numberStr, line, opts);
      currentNumber--;
      
      return formatted;
    });
  };

  const toRoman = (num: number): string => {
    const values = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
    const symbols = ['M', 'CM', 'D', 'CD', 'C', 'XC', 'L', 'XL', 'X', 'IX', 'V', 'IV', 'I'];
    let result = '';
    
    for (let i = 0; i < values.length; i++) {
      while (num >= values[i]) {
        result += symbols[i];
        num -= values[i];
      }
    }
    
    return result;
  };

  const toAlpha = (num: number, uppercase: boolean): string => {
    let result = '';
    let n = num - 1;
    
    while (n >= 0) {
      result = String.fromCharCode((uppercase ? 65 : 97) + (n % 26)) + result;
      n = Math.floor(n / 26) - 1;
    }
    
    return result;
  };

  const addGroupingSeparator = (str: string, groupSize: number, separator: string): string => {
    const reversed = str.split('').reverse();
    const groups = [];
    
    for (let i = 0; i < reversed.length; i += groupSize) {
      groups.push(reversed.slice(i, i + groupSize).join(''));
    }
    
    return groups.join(separator).split('').reverse().join('');
  };

  const calculateNumberingStats = (
    original: string, 
    numbered: string, 
    originalLines: string[], 
    numberedLines: string[], 
    maxNumber: number
  ): NumberingStats => {
    const emptyLines = originalLines.filter(line => line.trim() === '').length;
    const nonEmptyLines = originalLines.filter(line => line.trim() !== '');
    
    const longestLine = nonEmptyLines.reduce((longest, line) => 
      line.length > longest.length ? line : longest, ''
    );
    const shortestLine = nonEmptyLines.reduce((shortest, line) => 
      line.length < shortest.length ? line : shortest, nonEmptyLines[0] || ''
    );
    
    const averageLineLength = nonEmptyLines.length > 0 
      ? Math.round(nonEmptyLines.reduce((sum, line) => sum + line.length, 0) / nonEmptyLines.length)
      : 0;

    const totalCharacters = numbered.length;
    const addedCharacters = numbered.length - original.length;
    const characterIncrease = original.length > 0 ? Math.round((addedCharacters / original.length) * 100) : 0;

    return {
      totalLines: originalLines.length,
      numberedLines: numberedLines.length,
      emptyLines,
      longestLine,
      shortestLine,
      averageLineLength,
      maxLineNumber: maxNumber,
      totalCharacters,
      addedCharacters,
      characterIncrease
    };
  };

  const createEmptyStats = (): NumberingStats => ({
    totalLines: 0,
    numberedLines: 0,
    emptyLines: 0,
    longestLine: '',
    shortestLine: '',
    averageLineLength: 0,
    maxLineNumber: 0,
    totalCharacters: 0,
    addedCharacters: 0,
    characterIncrease: 0
  });

  const numberingResult: NumberingResult = useMemo(() => {
    return processNumbering(inputText, selectedStyle, options);
  }, [inputText, selectedStyle, options]);

  useEffect(() => {
    setOutputText(numberingResult.numberedText);
  }, [numberingResult]);

  const copyToClipboard = async () => {
    if (outputText) {
      await navigator.clipboard.writeText(outputText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadResult = () => {
    if (outputText) {
      const summary = `Line Numbering Results:
Style: ${numberingStyles.find(s => s.id === selectedStyle)?.name}
Total Lines: ${numberingResult.statistics.totalLines}
Numbered Lines: ${numberingResult.statistics.numberedLines}
Characters Added: ${numberingResult.statistics.addedCharacters}
Size Increase: ${numberingResult.statistics.characterIncrease}%

Numbered Text:
${outputText}`;

      const blob = new Blob([summary], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'numbered-lines.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateSampleText = () => {
    const samples = [
      `First line of text
Second line of text
Third line of text

Fifth line after empty line
Sixth line
Seventh line`,
      `function example() {
    let x = 1;
    let y = 2;
    return x + y;
}

console.log(example());`,
      `Chapter 1: Introduction
This is the first chapter.

Chapter 2: Getting Started
This chapter covers the basics.

Chapter 3: Advanced Topics
More complex material here.`,
      `Item one
Item two
Item three
Item four
Item five
Item six
Item seven
Item eight
Item nine
Item ten`,
      `To do today:
Buy groceries
Walk the dog
Finish project
Call mom
Read book
Exercise`,
      `Line A
Line B
Line C

Line E
Line F

Line H
Line I
Line J`
    ];
    setInputText(samples[Math.floor(Math.random() * samples.length)]);
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <Hash className="text-purple-600" />
            Advanced Line Numberer
          </h1>
          <p className="text-gray-600">Add line numbers with customizable styles and formatting options</p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Numbering Configuration</h2>
            <div className="flex gap-2">
              <button
                onClick={generateSampleText}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample Text
              </button>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Settings size={16} />
                Options
              </button>
              <button
                onClick={() => setShowStats(!showStats)}
                className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
              >
                <Eye size={16} />
                Statistics
              </button>
            </div>
          </div>

          <div className="text-center">
            <div className="text-sm text-gray-600 mb-2">
              Style: <span className="font-semibold">{numberingStyles.find(s => s.id === selectedStyle)?.name}</span>
            </div>
            <div className="text-sm text-gray-600">
              Lines: <span className="font-semibold text-purple-600">{numberingResult.statistics.totalLines}</span> 
              • Numbered: <span className="font-semibold text-blue-600">{numberingResult.statistics.numberedLines}</span>
            </div>
          </div>
        </div>

        {/* Numbering Options */}
        {showOptions && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Numbering Options</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Number Settings</h3>
                
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Start Number</label>
                  <input
                    type="number"
                    min="0"
                    value={options.startNumber}
                    onChange={(e) => setOptions({...options, startNumber: parseInt(e.target.value) || 1})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Increment</label>
                  <input
                    type="number"
                    min="1"
                    value={options.increment}
                    onChange={(e) => setOptions({...options, increment: parseInt(e.target.value) || 1})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.padWithZeros}
                    onChange={(e) => setOptions({...options, padWithZeros: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Pad with Zeros</span>
                </label>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Minimum Digits</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={options.minDigits}
                    onChange={(e) => setOptions({...options, minDigits: parseInt(e.target.value) || 1})}
                    disabled={!options.padWithZeros}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm disabled:bg-gray-100"
                  />
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.reverseNumbering}
                    onChange={(e) => setOptions({...options, reverseNumbering: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Reverse Numbering</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Formatting</h3>
                
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Prefix</label>
                  <input
                    type="text"
                    value={options.prefix}
                    onChange={(e) => setOptions({...options, prefix: e.target.value})}
                    placeholder="Text before number..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Suffix</label>
                  <input
                    type="text"
                    value={options.suffix}
                    onChange={(e) => setOptions({...options, suffix: e.target.value})}
                    placeholder="Text after number..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Separator</label>
                  <input
                    type="text"
                    value={options.separator}
                    onChange={(e) => setOptions({...options, separator: e.target.value})}
                    placeholder="Between number and line..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.addBrackets}
                    onChange={(e) => setOptions({...options, addBrackets: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Add Brackets</span>
                </label>

                {options.addBrackets && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Bracket Style</label>
                    <select
                      value={options.bracketStyle}
                      onChange={(e) => setOptions({...options, bracketStyle: e.target.value as any})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                    >
                      <option value="()">Parentheses ()</option>
                      <option value="[]">Square brackets []</option>
                      <option value="{}">Curly braces {}</option>
                      <option value="<>">Angle brackets &lt;&gt;</option>
                    </select>
                  </div>
                )}

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.addSpacing}
                    onChange={(e) => setOptions({...options, addSpacing: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Custom Spacing</span>
                </label>

                {options.addSpacing && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Space Count</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={options.spacingCount}
                      onChange={(e) => setOptions({...options, spacingCount: parseInt(e.target.value) || 4})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Line Handling</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.includeEmptyLines}
                    onChange={(e) => setOptions({...options, includeEmptyLines: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Include Empty Lines</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.skipEmptyLines}
                    onChange={(e) => setOptions({...options, skipEmptyLines: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Skip Empty Lines</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.onlyNumberNonEmpty}
                    onChange={(e) => setOptions({...options, onlyNumberNonEmpty: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Only Number Non-Empty</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.restartOnEmptyLine}
                    onChange={(e) => setOptions({...options, restartOnEmptyLine: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Restart on Empty Line</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.removeExistingNumbers}
                    onChange={(e) => setOptions({...options, removeExistingNumbers: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Remove Existing Numbers</span>
                </label>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Group Every (0 = disabled)</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={options.groupEvery}
                    onChange={(e) => setOptions({...options, groupEvery: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                {options.groupEvery > 0 && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Group Separator</label>
                    <input
                      type="text"
                      value={options.groupSeparator}
                      onChange={(e) => setOptions({...options, groupSeparator: e.target.value})}
                      placeholder="Grouping separator..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                    />
                  </div>
                )}

                {selectedStyle === 'custom' && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Custom Format</label>
                    <input
                      type="text"
                      value={options.customFormat}
                      onChange={(e) => setOptions({...options, customFormat: e.target.value})}
                      placeholder="Use {number}, {separator}, {line}"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Input and Output */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Original Text</label>
                <div className="text-sm text-gray-500">
                  {numberingResult.statistics.totalLines} lines
                </div>
              </div>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter text to add line numbers..."
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none resize-none text-gray-700 transition-colors font-mono text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Numbered Text</label>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-500">
                    {numberingResult.statistics.numberedLines} lines
                  </div>
                  <button
                    onClick={copyToClipboard}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Copy size={16} />
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadResult}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>
              <textarea
                value={outputText}
                readOnly
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none font-mono text-sm"
                placeholder="Numbered text will appear here..."
              />
            </div>
          </div>
        </div>

        {/* Numbering Styles */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Hash className="text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Numbering Style</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {numberingStyles.map((style) => (
              <div
                key={style.id}
                onClick={() => setSelectedStyle(style.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedStyle === style.id
                    ? `border-${style.color}-500 ${getColorClasses(style.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {style.icon}
                    <h3 className={`font-semibold ${
                      selectedStyle === style.id ? '' : 'text-gray-800'
                    }`}>
                      {style.name}
                    </h3>
                  </div>
                  {selectedStyle === style.id && (
                    <div className={`w-3 h-3 bg-${style.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm ${
                  selectedStyle === style.id ? '' : 'text-gray-600'
                }`}>
                  {style.description}
                </p>
              </div>
            ))}
          </div>

          {/* Style Help */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="font-semibold text-gray-700 mb-2">
              {numberingStyles.find(s => s.id === selectedStyle)?.name} Help
            </h3>
            <div className="text-sm text-gray-600">
              {selectedStyle === 'decimal' && (
                <div>
                  <p>• Standard decimal numbering: 1, 2, 3, 4, 5...</p>
                  <p>• Supports zero padding and grouping separators</p>
                </div>
              )}
              {selectedStyle === 'roman' && (
                <div>
                  <p>• Roman numeral numbering: I, II, III, IV, V...</p>
                  <p>• Traditional Roman numerals up to large numbers</p>
                </div>
              )}
              {selectedStyle === 'alpha' && (
                <div>
                  <p>• Lowercase alphabetic: a, b, c, d, e...</p>
                  <p>• Continues with aa, ab, ac after z</p>
                </div>
              )}
              {selectedStyle === 'alphaUpper' && (
                <div>
                  <p>• Uppercase alphabetic: A, B, C, D, E...</p>
                  <p>• Continues with AA, AB, AC after Z</p>
                </div>
              )}
              {selectedStyle === 'binary' && (
                <div>
                  <p>• Binary numbering: 1, 10, 11, 100, 101...</p>
                  <p>• Base-2 number system representation</p>
                </div>
              )}
              {selectedStyle === 'hexadecimal' && (
                <div>
                  <p>• Hexadecimal numbering: 1, 2, 3, 4, 5, 6, 7, 8, 9, A, B...</p>
                  <p>• Base-16 number system with uppercase letters</p>
                </div>
              )}
              {selectedStyle === 'octal' && (
                <div>
                  <p>• Octal numbering: 1, 2, 3, 4, 5, 6, 7, 10, 11...</p>
                  <p>• Base-8 number system representation</p>
                </div>
              )}
              {selectedStyle === 'bullets' && (
                <div>
                  <p>• Bullet point markers: • for each line</p>
                  <p>• Consistent bullet symbols instead of numbers</p>
                </div>
              )}
              {selectedStyle === 'dashes' && (
                <div>
                  <p>• Dash markers: - for each line</p>
                  <p>• Simple dash-based line marking</p>
                </div>
              )}
              {selectedStyle === 'asterisks' && (
                <div>
                  <p>• Asterisk markers: * for each line</p>
                  <p>• Star-based line marking system</p>
                </div>
              )}
              {selectedStyle === 'arrows' && (
                <div>
                  <p>• Arrow markers: → for each line</p>
                  <p>• Directional arrow indicators</p>
                </div>
              )}
              {selectedStyle === 'custom' && (
                <div>
                  <p>• Custom format using template variables</p>
                  <p>• Use <code className="bg-gray-200 px-1 rounded">{`{number}`}</code>, <code className="bg-gray-200 px-1 rounded">{`{separator}`}</code>, <code className="bg-gray-200 px-1 rounded">{`{line}`}</code> in custom format</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        {showStats && inputText && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-green-600" />
              <h2 className="text-xl font-semibold text-gray-800">Numbering Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Total Lines</h3>
                <div className="text-2xl font-bold text-blue-900">{numberingResult.statistics.totalLines}</div>
                <p className="text-sm text-blue-600">Original lines</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Numbered Lines</h3>
                <div className="text-2xl font-bold text-green-900">{numberingResult.statistics.numberedLines}</div>
                <p className="text-sm text-green-600">Lines with numbers</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">Characters Added</h3>
                <div className="text-2xl font-bold text-orange-900">{numberingResult.statistics.addedCharacters}</div>
                <p className="text-sm text-orange-600">New characters</p>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Size Increase</h3>
                <div className="text-2xl font-bold text-purple-900">{numberingResult.statistics.characterIncrease}%</div>
                <p className="text-sm text-purple-600">Text expansion</p>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Empty Lines</h4>
                <div className="text-lg font-bold text-gray-900">{numberingResult.statistics.emptyLines}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Max Line Number</h4>
                <div className="text-lg font-bold text-gray-900">{numberingResult.statistics.maxLineNumber}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Avg Line Length</h4>
                <div className="text-lg font-bold text-gray-900">{numberingResult.statistics.averageLineLength}</div>
              </div>
            </div>

            {/* Line Analysis */}
            {numberingResult.statistics.longestLine && numberingResult.statistics.shortestLine && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-700 mb-3">Line Analysis:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h4 className="font-semibold text-emerald-800 mb-1">
                      Longest Line ({numberingResult.statistics.longestLine.length} chars)
                    </h4>
                    <div className="text-sm text-emerald-700 font-mono truncate max-w-full">
                      {numberingResult.statistics.longestLine}
                    </div>
                  </div>
                  <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <h4 className="font-semibold text-teal-800 mb-1">
                      Shortest Line ({numberingResult.statistics.shortestLine.length} chars)
                    </h4>
                    <div className="text-sm text-teal-700 font-mono truncate max-w-full">
                      {numberingResult.statistics.shortestLine || '(empty)'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Preview */}
        {inputText && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <List className="text-indigo-600" />
              <h2 className="text-xl font-semibold text-gray-800">Numbering Preview</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Original Text (first 10 lines)</h3>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg font-mono text-sm max-h-64 overflow-auto">
                  {inputText.split('\n').slice(0, 10).map((line, index) => (
                    <div key={index} className="whitespace-pre-wrap break-words">
                      {line || '(empty line)'}
                    </div>
                  ))}
                  {inputText.split('\n').length > 10 && (
                    <div className="text-gray-500 italic">
                      ...and {inputText.split('\n').length - 10} more lines
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Numbered Text (first 10 lines)</h3>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg font-mono text-sm max-h-64 overflow-auto">
                  {outputText.split('\n').slice(0, 10).map((line, index) => (
                    <div key={index} className="whitespace-pre-wrap break-words">
                      {line || '(empty line)'}
                    </div>
                  ))}
                  {outputText.split('\n').length > 10 && (
                    <div className="text-gray-500 italic">
                      ...and {outputText.split('\n').length - 10} more lines
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Numbering Summary */}
            <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <h3 className="font-semibold text-indigo-700 mb-2">Numbering Summary</h3>
              <div className="text-sm text-indigo-800 space-y-1">
                <div>Style: <span className="font-semibold">{numberingStyles.find(s => s.id === selectedStyle)?.name}</span></div>
                <div>Range: <span className="font-semibold">{options.startNumber} to {numberingResult.statistics.maxLineNumber}</span> (increment: {options.increment})</div>
                <div>Format: <span className="font-mono bg-white px-2 py-1 rounded">
                  {options.prefix}
                  {options.addBrackets ? options.bracketStyle[0] : ''}
                  {selectedStyle === 'decimal' ? '1' : selectedStyle === 'roman' ? 'I' : selectedStyle === 'alpha' ? 'a' : selectedStyle === 'alphaUpper' ? 'A' : '1'}
                  {options.addBrackets ? options.bracketStyle[1] : ''}
                  {options.suffix}
                  {options.separator}
                  line content
                </span></div>
                <div>Processing: {numberingResult.statistics.totalLines} → {numberingResult.statistics.numberedLines} lines (+{numberingResult.statistics.addedCharacters} chars)</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LineNumberer;